import { mapEntity } from '@api/mappers/mapEntity';
import type { PriceHoverData } from '@chart';
import type { HitTestResult } from '@chartv2';
import { isSnakeWithThreshold } from '@helpers/utils/entity/entity';
import { IndicatorLegendDetails } from '@terminal/views/turbo-chart/components/indicator-legend-details/IndicatorLegendDetails';
import { LegendPrice } from '@terminal/views/turbo-chart/components/legend-price/LegendPrice';
import type { TerminalChartAssetData } from '@terminal/views/turbo-chart/utils/chart-utils/types';

export interface SeriesLegendStatusProps {
  asset: TerminalChartAssetData;
  isPrimary: boolean;
  legendColor: string;
  hoverHit?: HitTestResult;
}

const toHoverData = (hoverHit: HitTestResult) => {
  const { point } = hoverHit;
  const time = new Date(hoverHit.point.time).toISOString();
  const currentPrice =
    'close' in point
      ? { ...point, time }
      : {
          time,
          open: point.value,
          high: point.value,
          low: point.value,
          close: point.value,
        };

  return {
    currentPrice,
  };
};

/**
 * Right-hand side of the chartv2 legend row. Threshold indicators reuse
 * v1's `IndicatorLegendDetails` (value + bullish/bearish levels); other
 * series keep `LegendPrice` for live/last-close formatting.
 *
 * When `hoverHit` is provided (from the chart engine's crosshair
 * hit-test), the displayed value tracks the hovered data point.
 */
export const SeriesLegendStatus = ({
  asset,
  isPrimary,
  legendColor,
  hoverHit,
}: SeriesLegendStatusProps) => {
  const { entity, snakeMeta, ts, seriesType } = asset;

  if (!snakeMeta || ts.length === 0) {
    return null;
  }

  const hoverData = hoverHit ? toHoverData(hoverHit) : undefined;

  if (isSnakeWithThreshold(snakeMeta)) {
    return (
      <IndicatorLegendDetails
        chartAssetData={asset}
        hoverData={hoverData as PriceHoverData | undefined}
      />
    );
  }

  return (
    <LegendPrice
      entity={mapEntity(entity)}
      snakeMeta={snakeMeta}
      ts={ts}
      legendColor={legendColor}
      isPrimary={isPrimary}
      isChartHovered={!!hoverHit}
      selectedSeriesType={seriesType}
      hoverData={hoverData}
    />
  );
};
